import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  Save,
  X,
  Table as TableIcon,
  Users,
  AlertCircle,
  Info,
  FileText,
  ArrowLeft,
} from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import {
  server_post_data,
  save_update_table,
  get_all_tables_res,
} from "../ServiceConnection/serviceconnection";
import Loader from "../components/layout/Loader";

export default function AddEditTable() {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  // State management
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [errors, setErrors] = useState({});

  // Form data
  const [formData, setFormData] = useState({
    tableInput: "",
    maxPerson: "",
    description: "",
  });

  const [generatedTables, setGeneratedTables] = useState([]);
  const primary_id = location.state?.primary_id;

  // Handle field change
  const handleFieldChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const { [field]: removed, ...rest } = prev;
      return rest;
    });
  }, []);

  // Parse table input and generate table names
  const parseTableInput = useCallback((inputString) => {
    if (!inputString || typeof inputString !== "string") {
      setGeneratedTables([]);
      return;
    }

    let updatedTableNames = [];
    const parts = inputString.split(",").map((part) => part.trim());

    parts.forEach((part) => {
      if (part.includes(":")) {
        const rangeParts = part.split(":");

        if (rangeParts.length === 2) {
          // Updated regex: (.+?) captures any characters (including spaces) before the number
          const prefixMatch = rangeParts[0].trim().match(/^(.+?)(\d+)$/);
          const endMatch = rangeParts[1].trim().match(/^(.+?)?(\d+)$/);

          if (prefixMatch && endMatch) {
            const prefix = prefixMatch[1]; // This will preserve spaces
            const startNum = parseInt(prefixMatch[2], 10);
            const endNum = parseInt(endMatch[2], 10);

            for (let i = startNum; i <= endNum; i++) {
              updatedTableNames.push(`${prefix}${i}`);
            }
          }
        } else if (rangeParts.length === 3) {
          const prefix = rangeParts[0].trim();
          const startNum = parseInt(rangeParts[1].trim(), 10);
          const endNum = parseInt(rangeParts[2].trim(), 10);

          if (!isNaN(startNum) && !isNaN(endNum)) {
            for (let i = startNum; i <= endNum; i++) {
              updatedTableNames.push(`${prefix}${i}`);
            }
          }
        }
      } else {
        updatedTableNames.push(part);
      }
    });
    console.log("Generated Tables:", updatedTableNames);
    setGeneratedTables(updatedTableNames);
  }, []);

  // Handle table input change
  const handleTableInputChange = useCallback(
    (value) => {
      handleFieldChange("tableInput", value);
      parseTableInput(value);
    },
    [handleFieldChange, parseTableInput],
  );

  // Fetch table data for edit
  const fetchTableData = useCallback(async () => {
    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("flag", 2);
    fd.append("call_id", primary_id);

    try {
      const response = await server_post_data(get_all_tables_res, fd);

      if (!response.data.error) {
        const tableData = response.data.message.data[0];
        setFormData({
          tableInput: tableData.table_code,
          maxPerson: tableData.max_person || "",
          description: "",
        });
        setGeneratedTables([tableData.table_code]);
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching table:", error);
      toast({
        title: "Error",
        description: "Failed to load table data",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [primary_id, toast]);

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.tableInput.trim()) {
      newErrors.tableInput = "Table number is required";
    }

    if (generatedTables.length === 0) {
      newErrors.tableInput = "Please enter valid table number(s)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, generatedTables]);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fill all required fields",
        variant: "destructive",
      });
      return;
    }

    setShowLoaderAdmin(true);

    const fd = new FormData();

    if (primary_id) {
      fd.append("main_id", primary_id);
    }

    fd.append("table_name", generatedTables.join(","));
    fd.append("table_size", formData.maxPerson);
    fd.append("table_desc", formData.description);

    try {
      const response = await server_post_data(save_update_table, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: primary_id
            ? "Table updated successfully"
            : "Tables created successfully",
        });
        navigate("/table-management");
      }
    } catch (error) {
      console.error("Save error:", error);
      toast({
        title: "Error",
        description: "Failed to save table",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [formData, generatedTables, primary_id, validateForm, toast, navigate]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    navigate("/table-management");
  }, [navigate]);

  // Initial load for edit
  useEffect(() => {
    if (primary_id) {
      fetchTableData();
    }
  }, [primary_id, fetchTableData]);

  return (
    <MainLayout>
      <Header
        title={primary_id ? "Edit Table" : "Add New Table"}
        subtitle={
          primary_id
            ? "Update table information"
            : "Create new table or multiple tables"
        }
      />

      <div className="p-6">
        <div className="mx-auto space-y-6">
          <Button
            variant="ghost"
            onClick={handleCancel}
            className="gap-2 -ml-2"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Recipes
          </Button>
          {/* Form Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <TableIcon className="w-4 h-4 text-primary" />
                Table Information
              </CardTitle>
              <CardDescription>
                Enter table details. You can create multiple tables at once
                using ranges.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Table Number/Range */}
                <div className="space-y-2">
                  <Label>
                    Table Number <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    placeholder="e.g., A10:A20 or T1,T2,T3"
                    value={formData.tableInput}
                    onChange={(e) => handleTableInputChange(e.target.value)}
                    className={errors.tableInput ? "border-destructive" : ""}
                  />
                  {errors.tableInput && (
                    <p className="text-xs text-destructive flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.tableInput}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Use comma (,) for multiple tables or colon (:) for ranges
                  </p>
                </div>

                {/* Capacity */}
                <div className="space-y-2">
                  <Label>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Number of Persons
                    </div>
                  </Label>
                  <Input
                    type="number"
                    placeholder="e.g., 4"
                    maxLength={5}
                    value={formData.maxPerson}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*$/.test(value)) {
                        handleFieldChange("maxPerson", value);
                      }
                    }}
                  />
                  <p className="text-xs text-muted-foreground">
                    Maximum seating capacity per table
                  </p>
                </div>
              </div>

              {/* Generated Tables Preview */}
              {generatedTables.length > 0 && (
                <div className="space-y-2">
                  <Label>
                    Generated Tables Preview ({generatedTables.length})
                  </Label>
                  <div className="p-4 rounded-lg border border-border bg-muted/30">
                    <div className="flex flex-wrap gap-2">
                      {generatedTables.map((table, index) => (
                        <div
                          key={index}
                          className="px-3 py-1 rounded-md bg-primary/10 text-primary text-sm font-medium"
                        >
                          {table}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Instructions Card */}
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Info className="w-5 h-5 text-primary" />
                </div>
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-foreground">
                    How to Create Multiple Tables
                  </h4>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-start gap-2">
                      <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-semibold text-primary">
                          1
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-foreground">
                          Table Range
                        </p>
                        <p>
                          Format:{" "}
                          <code className="bg-muted px-1 py-0.5 rounded">
                            A10:A20
                          </code>
                        </p>
                        <p className="text-xs">
                          Creates: A10, A11, A12, ... A20
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2">
                      <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-semibold text-primary">
                          2
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-foreground">
                          Multiple Tables with Range
                        </p>
                        <p>
                          Format:{" "}
                          <code className="bg-muted px-1 py-0.5 rounded">
                            AA10:AA20,BB1
                          </code>
                        </p>
                        <p className="text-xs">Creates: AA10-AA20 and BB1</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2">
                      <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-semibold text-primary">
                          3
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-foreground">
                          Multiple Individual Tables
                        </p>
                        <p>
                          Format:{" "}
                          <code className="bg-muted px-1 py-0.5 rounded">
                            T1,T2,T3
                          </code>
                        </p>
                        <p className="text-xs">Creates: T1, T2, T3</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2">
                      <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-semibold text-primary">
                          4
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-foreground">
                          Alternative Range Format
                        </p>
                        <p>
                          Format:{" "}
                          <code className="bg-muted px-1 py-0.5 rounded">
                            A:10:20
                          </code>
                        </p>
                        <p className="text-xs">
                          Creates: A10, A11, A12, ... A20
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-4 pt-4 border-t">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={showLoaderAdmin}
              className="gap-2"
            >
              <X className="w-4 h-4" />
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={showLoaderAdmin || generatedTables.length === 0}
              className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
            >
              {showLoaderAdmin ? (
                "Saving..."
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {primary_id ? "Update Table" : "Create Tables"}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
      {showLoaderAdmin && <Loader />}
    </MainLayout>
  );
}
